import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { LocateFixed, MapPin } from "lucide-react";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { useIsQrOwner } from "../hooks/useIsQrOwner";
import EmergencyLayout from "../components/layout/EmergencyLayout";
import BottomSheet from "../components/ui/bottom-sheet";
import { Button } from "../components/ui/button";
import { ICON_STROKE } from "../components/ui/icon";
import NearbySearchPanel from "../components/nearby/NearbySearchPanel";
import NearbyCategoryChips from "../components/nearby/NearbyCategoryChips";
import NearbyMapPreview from "../components/nearby/NearbyMapPreview";
import NearbyQuickEmergencyBar from "../components/nearby/NearbyQuickEmergencyBar";
import NearbyServiceCard from "../components/nearby/NearbyServiceCard";
import NearbyEmptyState from "../components/nearby/NearbyEmptyState";
import NearbySkeleton from "../components/nearby/NearbySkeleton";
import NearbyFloatingHelp from "../components/nearby/NearbyFloatingHelp";
import NearbyBottomNav from "../components/nearby/NearbyBottomNav";
import { formatLastUpdated, getMapPreviewUrl } from "../components/nearby/nearby-utils";

const NearbyServicesPage = () => {
  const { qrId } = useParams();
  const { token } = useAuth();
  const isOwner = useIsQrOwner(qrId);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState("all");
  const [mode, setMode] = useState("gps");
  const [pincode, setPincode] = useState("");
  const [searchRadius, setSearchRadius] = useState(null);
  const [locationGranted, setLocationGranted] = useState(false);
  const [callConfirm, setCallConfirm] = useState(null);
  const [coords, setCoords] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchByGPS = useCallback(async (lat, lng, type) => {
    setLoading(true);
    setError(null);
    try {
      const params = { lat, lng, radius: 10000, limit: 50 };
      if (type && type !== "all") params.type = type;
      const res = await api.get("/emergency-services/nearby", { params });
      setServices(res.data.services || []);
      setSearchRadius(res.data.searchRadius);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch nearby services");
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchByPincode = useCallback(async (code, type) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (type && type !== "all") params.type = type;
      const res = await api.get(`/emergency-services/by-pincode/${code}`, { params });
      setServices(res.data.services || []);
      setSearchRadius(null);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch services for this pincode");
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setMode("pincode");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lng: longitude });
        setLocationGranted(true);
        fetchByGPS(latitude, longitude, "all");
      },
      () => {
        setMode("pincode");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [fetchByGPS]);

  useEffect(() => {
    if (mode === "gps" && coords) {
      fetchByGPS(coords.lat, coords.lng, activeType);
    } else if (mode === "pincode" && pincode.length === 6) {
      fetchByPincode(pincode, activeType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const handlePincodeSubmit = (e) => {
    e.preventDefault();
    if (pincode.length !== 6) return;
    setMode("pincode");
    fetchByPincode(pincode, activeType);
  };

  const handleSwitchToGPS = () => {
    if (!navigator.geolocation) return;
    setMode("gps");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lng: longitude });
        setLocationGranted(true);
        fetchByGPS(latitude, longitude, activeType);
      },
      () => {
        setMode("pincode");
        setLoading(false);
        setError("Location permission denied. Please search by pincode.");
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleModeChange = (newMode) => {
    if (newMode === "gps") {
      if (locationGranted && coords) {
        setMode("gps");
        setError(null);
        fetchByGPS(coords.lat, coords.lng, activeType);
      } else {
        handleSwitchToGPS();
      }
      return;
    }

    setMode("pincode");
    setError(null);
    if (pincode.length === 6) {
      fetchByPincode(pincode, activeType);
    }
  };

  const handleSearchAnotherArea = () => {
    setMode("pincode");
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOpenMap = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const filtered = services;
  const backPath = qrId ? `/${qrId}/public` : "/";
  const mapUrl = useMemo(() => getMapPreviewUrl(filtered, coords), [filtered, coords]);
  const showPincodePrompt = mode === "pincode" && pincode.length !== 6 && !loading && !error;
  const hasGeolocation = typeof navigator !== "undefined" && Boolean(navigator.geolocation);
  const showMapPreview = !loading && (locationGranted || filtered.length > 0);

  const headerRight = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="lg:hidden min-h-10"
      onClick={handleSwitchToGPS}
      disabled={!hasGeolocation}
    >
      <LocateFixed size={16} strokeWidth={ICON_STROKE} />
      Locate
    </Button>
  );

  return (
    <EmergencyLayout
      title="Nearby Services"
      showBack
      backTo={backPath}
      showLanguage={false}
      maxWidth="max-w-[1100px]"
      headerRight={headerRight}
      className={qrId ? "nearby-has-bottom-nav" : undefined}
      contentClassName="nearby-page"
      footer={qrId ? <NearbyBottomNav qrId={qrId} isOwner={isOwner} isAuthenticated={Boolean(token)} /> : null}
    >
      <header className="nearby-hero hidden lg:block">
        <div className="nearby-hero__icon" aria-hidden>
          <MapPin size={22} strokeWidth={ICON_STROKE} />
        </div>
        <h1 className="nearby-hero__title">Nearby Emergency Services</h1>
        <p className="nearby-hero__subtitle">
          Find hospitals, ambulances, police stations, blood banks and emergency services near you.
        </p>
        {mode === "gps" && locationGranted && (
          <p className="nearby-hero__meta">Updated using your current location.</p>
        )}
      </header>

      <div className="nearby-stack">
        <NearbySearchPanel
          mode={mode}
          onModeChange={handleModeChange}
          pincode={pincode}
          onPincodeChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
          onPincodeSubmit={handlePincodeSubmit}
          onLocateMe={handleSwitchToGPS}
          locationGranted={locationGranted}
          searchRadius={searchRadius}
          hasGeolocation={hasGeolocation}
          lastUpdated={lastUpdated}
        />

        <NearbyCategoryChips activeType={activeType} onChange={setActiveType} />

        <NearbyQuickEmergencyBar onCall={setCallConfirm} variant="inline" />

        {showMapPreview && (
          <NearbyMapPreview
            services={filtered.length > 0 ? filtered : []}
            mapUrl={mapUrl}
            onOpenMap={handleOpenMap}
          />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="nearby-summary">
            <p className="nearby-summary__primary">
              <strong>{filtered.length}</strong> emergency service{filtered.length !== 1 ? "s" : ""} nearby
            </p>
            <p className="nearby-summary__secondary">
              Sorted by nearest distance
              {lastUpdated && (
                <>
                  <span aria-hidden> · </span>
                  {formatLastUpdated(lastUpdated)}
                </>
              )}
            </p>
          </div>
        )}

        {loading ? (
          <NearbySkeleton />
        ) : error ? (
          <div className="nearby-error-wrap">
            <p className="nearby-error-wrap__message">{error}</p>
            <NearbyEmptyState
              onUseLocation={hasGeolocation ? handleSwitchToGPS : null}
              onSearchAnotherArea={handleSearchAnotherArea}
            />
          </div>
        ) : showPincodePrompt ? (
          <NearbyEmptyState variant="pincode" />
        ) : filtered.length === 0 ? (
          <NearbyEmptyState
            onUseLocation={hasGeolocation ? handleSwitchToGPS : null}
            onSearchAnotherArea={handleSearchAnotherArea}
          />
        ) : (
          <div className="nearby-results">
            {filtered.map((svc, idx) => (
              <NearbyServiceCard key={`${svc.name}-${svc.pincode || idx}`} service={svc} onCall={setCallConfirm} />
            ))}
          </div>
        )}
      </div>

      {!callConfirm && <NearbyQuickEmergencyBar onCall={setCallConfirm} variant="mobile-bar" />}

      <NearbyFloatingHelp onCall={setCallConfirm} />

      <BottomSheet
        open={Boolean(callConfirm)}
        onClose={() => setCallConfirm(null)}
        title={`Call ${callConfirm?.name}?`}
        overlayClassName={
          qrId
            ? "max-lg:pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))]"
            : "max-lg:pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))]"
        }
        secondaryAction={{ label: "Cancel", onClick: () => setCallConfirm(null) }}
        primaryAction={{
          label: "Call now",
          variant: "success",
          onClick: () => {
            if (callConfirm) window.location.href = `tel:${callConfirm.number}`;
            setCallConfirm(null);
          },
        }}
      >
        <p className="text-lg font-mono font-bold text-foreground">{callConfirm?.number}</p>
      </BottomSheet>
    </EmergencyLayout>
  );
};

export default NearbyServicesPage;
